import { useState } from 'react';
import Button from '@mui/material/Button';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ImageViewer from './ImageViewer';
import { getCanvasImgData } from '../utils/api';

const ImageComponent = ({
  imageUrl,
  kernelDetails,
  classNameCard,
  imageUrlForBottom,
  ledImageUrl,
  ledImageUrlForBottom,
  encodings,
  scanDetails,
  setProcessData,
  setKernalDetails,
  bbox,
  bboxBottom
}) => {

  const [changedClassName, setChangeClassName] = useState(classNameCard);
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [multipleBoxesUrl, setMultipleBoxesUrl] = useState('');
  const [multipleBoxesUrlForBottom, setMultipleBoxesUrlForBottom] = useState('');
  const [ledMultipleBoxesUrl, setLedMultipleBoxesUrl] = useState('');
  const [ledMultipleBoxesUrlForBottom, setLedMultipleBoxesUrlForBottom] = useState('');

  const handleButtonSelect = (newClassName) => {
    const oldClassName = changedClassName;
    if (oldClassName === newClassName) return;
    if (!bbox) return;

    setChangeClassName(newClassName);

    setKernalDetails((prev) => {
      const updatedCounts = { ...prev.kernel_counts };
      if (updatedCounts[oldClassName] > 0) updatedCounts[oldClassName] -= 1;
      if (updatedCounts[newClassName] !== undefined) updatedCounts[newClassName] += 1;
      return { ...prev, kernel_counts: updatedCounts };
    });

    setProcessData((prev) => {
      const updatedBboxes = { ...prev.bboxes };
      const updatedBboxesBottom = { ...prev.bboxes_bottom };

      const oldTopArray = [...(updatedBboxes[oldClassName] || [])];
      const newTopArray = [...(updatedBboxes[newClassName] || [])];

      const topIndex = bbox?.id
        ? oldTopArray.findIndex((item) => item.id === bbox.id)
        : oldTopArray.findIndex((item) =>
          item[0] === bbox[0] && item[1] === bbox[1] &&
          item[2] === bbox[2] && item[3] === bbox[3]
        );

      if (topIndex === -1) return prev;

      const [movedTopBox] = oldTopArray.splice(topIndex, 1);
      newTopArray.push(movedTopBox);
      updatedBboxes[oldClassName] = oldTopArray;
      updatedBboxes[newClassName] = newTopArray;

      // ===== BOTTOM =====
      if (updatedBboxesBottom && bboxBottom) {
        const oldBottomArray = [...(updatedBboxesBottom[oldClassName] || [])];
        const newBottomArray = [...(updatedBboxesBottom[newClassName] || [])];

        const bottomIndex = bboxBottom?.id
          ? oldBottomArray.findIndex((item) => item.id === bboxBottom.id)
          : oldBottomArray.findIndex((item) =>
            item[0] === bboxBottom[0] && item[1] === bboxBottom[1] &&
            item[2] === bboxBottom[2] && item[3] === bboxBottom[3]
          );

        if (bottomIndex !== -1) {
          const [movedBottomBox] = oldBottomArray.splice(bottomIndex, 1);
          newBottomArray.push(movedBottomBox);
          updatedBboxesBottom[oldClassName] = oldBottomArray;
          updatedBboxesBottom[newClassName] = newBottomArray;
        }
      }

      return { ...prev, bboxes: updatedBboxes, bboxes_bottom: updatedBboxesBottom };
    });
  };

  const showBoundingBoxes = async () => {
    setOpen(true);
    setIsLoading(true);

    const files = scanDetails?.scan_files || [];

    // ===== FILES =====
    const capturedTopFile =
      files.find(f =>
        ['captured.webp', 'captured.png', 'captured.jpeg', 'captured.jpg'].includes(f.file_name)
      ) || files.find(f => f.file_name.includes('_cv_server'));

    const capturedTopUrl = capturedTopFile?.file_url || null;

    const capturedBottomFile =
      files.find(f => f.file_name.includes('captured_bottom')) ||
      files.find(f => f.file_name.includes('_bottom_cv_server'));

    const capturedBottomUrl = capturedBottomFile?.file_url || null;

    const ledTopFile = files.find(f => f.file_name?.includes('captured_led'));
    const ledTopUrl = ledTopFile?.file_url || null;

    const ledBottomFile = files.find(f => f.file_name?.includes('captured_bottom_led'));
    const ledBottomUrl = ledBottomFile?.file_url || null;

    let boxesUrl, boxesUrlForBottom, ledBoxesUrl, ledBoxesUrlForBottom;

    // ===== USE DIRECT bbox =====
    if (bbox && capturedTopUrl) {
      boxesUrl = await getCanvasImgData(
        capturedTopUrl,
        JSON.stringify(bbox)
      );
    }

    if (bboxBottom && capturedBottomUrl) {
      boxesUrlForBottom = await getCanvasImgData(
        capturedBottomUrl,
        JSON.stringify(bboxBottom)
      );
    }

    if (bbox && ledTopUrl) {
      ledBoxesUrl = await getCanvasImgData(
        ledTopUrl,
        JSON.stringify(bbox)
      );
    }

    if (bboxBottom && ledBottomUrl) {
      ledBoxesUrlForBottom = await getCanvasImgData(
        ledBottomUrl,
        JSON.stringify(bboxBottom)
      );
    }

    setMultipleBoxesUrl(boxesUrl);
    setMultipleBoxesUrlForBottom(boxesUrlForBottom);
    setLedMultipleBoxesUrl(ledBoxesUrl);
    setLedMultipleBoxesUrlForBottom(ledBoxesUrlForBottom);

    setIsLoading(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          width: imageUrlForBottom ? "400px" : "200px",
          border: classNameCard === changedClassName ? "1px solid black" : "4px solid red",
          position: "relative",
          display: imageUrlForBottom ? "flex" : "block",
          flexWrap: "wrap",
          paddingBottom: "40px",
        }}
      >
        {/* ===== NORMAL TOP ===== */}
        <div style={{ width: imageUrlForBottom ? "50%" : "100%", height: "200px" }}>
          <img src={imageUrl} alt="Top view" style={{ maxHeight: "80%", maxWidth: "80%" }} />
          {imageUrlForBottom && (
            <div style={{ fontSize: 14, textAlign: 'center', marginTop: 2 }}>TOP</div>
          )}
        </div>

        {/* ===== NORMAL BOTTOM ===== */}
        {imageUrlForBottom && (
          <div style={{ width: "50%", height: "200px" }}>
            <img src={imageUrlForBottom} alt="Bottom view" style={{ maxHeight: "80%", maxWidth: "80%" }} />
            <div style={{ fontSize: 14, textAlign: 'center', marginTop: 2 }}>BOTTOM</div>
          </div>
        )}

        {/* ===== LED TOP ===== */}
        {ledImageUrl && (
          <div style={{ width: imageUrlForBottom ? "50%" : "100%", height: "200px" }}>
            <img src={ledImageUrl} alt="LED Top view" style={{ maxHeight: "80%", maxWidth: "80%" }} />
            <div style={{ fontSize: 14, textAlign: 'center', marginTop: 2 }}>TOP BOTTOM LED</div>
          </div>
        )}

        {/* ===== LED BOTTOM ===== */}
        {ledImageUrlForBottom && (
          <div style={{ width: "50%", height: "200px" }}>
            <img src={ledImageUrlForBottom} alt="LED Bottom view" style={{ maxHeight: "80%", maxWidth: "80%" }} />
            <div style={{ fontSize: 14, textAlign: 'center', marginTop: 2 }}>BOTTOM TOP LED</div>
          </div>
        )}

        <div
          className="icon-container"
          onClick={showBoundingBoxes}
          style={{
            position: "absolute",
            bottom: "8px",
            left: "8px",
            cursor: "pointer",
          }}
        >
          <ArrowForwardIcon />
        </div>
      </div>

      {/* ===== CLASS BUTTONS ===== */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${imageUrlForBottom ? 3 : 2}, 1fr)`,
          gap: '10px',
          padding: '20px',
        }}
      >
        {kernelDetails?.classes?.map((className) => (
          <Button
            key={className}
            variant="outlined"
            style={{
              border: changedClassName === className ? '3px solid green' : '1px solid #1976d2',
            }}
            onClick={() => handleButtonSelect(className)}
          >
            {encodings[className]}
          </Button>
        ))}
      </div>
      {open && (
        <ImageViewer
          open={open}
          isLoading={isLoading}
          onClose={() => setOpen(false)}
          title="Image Viewer"
          imageUrl={multipleBoxesUrl}
          imageUrlForBottom={multipleBoxesUrlForBottom}
          ledMultipleBoxesUrl={ledMultipleBoxesUrl}
          ledMultipleBoxesUrlForBottom={ledMultipleBoxesUrlForBottom}
        />
      )}
    </div>
  );
};

export default ImageComponent;